import { useFieldArray, useForm } from "react-hook-form";
import { FiChevronDown, FiChevronUp, FiPlusCircle, FiTrash2 } from "react-icons/fi";
import { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { deleteTutorDocument, updateTutorDocuments } from "../../store/profile/profileActions";
import { CheckboxInput } from "../inputs/checkbox-input";
import { successToast } from "../../utils";
import { selectTutorProfile, TutorDocumentsType } from "../../store/profile/profileSlice";
import { CertificateInput } from "../profile/inputs/certificate-input";

const emptyExperienceRow = () => ({
    _id: "",
    label: "",
    from: "",
    to: "",
    file: "",
    description: "",
    school: "",
});

const TutorExperience = ({ setCurrentStep, setActiveTab }) => {
    const dispatch = useDispatch();
    const tutor = useSelector((state) => state.profile.currentProfile);
    const tutorProfile = useSelector(selectTutorProfile);

    const storedExperience =
        tutorProfile?.experience ?? tutor?.experience ?? [];

    const [removeSelected, setRemoveSelected] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        getValues,
        control,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            noExperience: tutor?.noExperience,
            experience:
                tutor?.noExperience || (tutor?.experience?.length ?? 0) > 0
                    ? []
                    : [emptyExperienceRow()],
        }
    });

    const {
        fields: certificateFields,
        append: certificateAppend,
        remove: certificateRemove,
        replace: certificateReplace,
    } = useFieldArray({
        control,
        name: "experience",
    });

    const formRef = useRef(null);

    const removeCertif = async (id) => {
        await dispatch(deleteTutorDocument({ id, name: "experiences" }))
            .unwrap()
            .then(() => setRemoveSelected(-1));
    };

    const Remove = ({ index, id }) => {
        if (index === removeSelected) {
            return (
                <div className="flex items-center">
                    <p className='mr-1'>Remove it ?</p>
                    <button className='mr-1' onClick={(e) => { e.preventDefault(); removeCertif(id) }}>Yes</button>
                    <button className='mr-1' onClick={(e) => { e.preventDefault(); setRemoveSelected(-1) }}>No</button>
                </div>
            );
        }
        return (
            <button type="button" onClick={() => { setRemoveSelected(index) }}>
                <FiTrash2 size={20} color='#df2b47' />
            </button>
        );
    };

    const CertifElement = ({ element, index }) => {
        return (
            <div className="border-2  flex flex-col justify-between mb-2 rounded-xl p-3">
                <div className="flex justify-between items-start">
                    <h6 className="text-nt-blue">{element.label}</h6>
                    <Remove index={index} id={element._id || element.id} />
                </div>
                <span className="min-w-[150px] text-nt-blue">
                    {element.from} - {element.toPresent ? "Present" : element.to}
                </span>
                <span className="text-nt-grey">
                    {element.company}
                </span>
                <p className="text-[14px]">{element.description}</p>
            </div>
        );
    };

    const noExperience = watch("noExperience");

    const onSubmit = async (data) => {
        setIsLoading(true);
        await dispatch(updateTutorDocuments({
            documents: data.experience,
            noDocument: data.noExperience,
            type: TutorDocumentsType.experience,
        }))
            .unwrap()
            .then((responseData) => {
                setIsLoading(false);
                successToast("Modification applied successfully");
                if (responseData !== undefined && responseData.length && certificateFields.length > 0) {
                    certificateReplace([]);
                }
            });
    };

    const addEmptyCertificate = () => {
        certificateAppend(emptyExperienceRow());
    };

    useEffect(() => {
        if (!tutor) return;
        const serverStored = tutor.experience ?? [];
        reset({
            noExperience: tutor.noExperience,
            experience:
                tutor.noExperience
                    ? []
                    : serverStored.length > 0
                      ? []
                      : [emptyExperienceRow()],
        });
    }, [tutor?._id, tutor?.experience?.length, tutor?.noExperience, reset]);

    // useEffect(() => {
    //     const storedCount = storedExperience.length;
    //     if (!noExperience && certificateFields.length === 0 && storedCount === 0) {
    //         addEmptyCertificate();
    //     }
    // }, [noExperience, storedExperience.length, certificateFields.length]);

    return (
        <div className="rounded-2xl bg-white px-5 py-3 mb-5">
            <div onClick={() => setIsOpen((v) => !v)} className={`cursor-pointer flex items-center justify-between font-bold text-nt-blue border-color-nt-grey ${isOpen && "border-b-2 mb-4 pb-2 "}`}>
                Experience
                {isOpen ? <FiChevronUp /> : <FiChevronDown />}
            </div>
            <div className={isOpen ? "block" : "hidden"}>
                <form ref={formRef} id="teacher-experience" className="flex flex-col [&_h2]:mb-5 [&_h2]:text-xl [&_h2]:text-nt-blue" onSubmit={handleSubmit(onSubmit)}>
                    <CheckboxInput
                        name="noExperience"
                        label="I don't have work experience"
                        register={register}
                        control={control}
                        style={{ marginBottom: "20px" }}
                    />

                    <div className="">
                        {!noExperience &&
                            storedExperience.map((el, index) => (
                                <CertifElement
                                    element={el}
                                    key={el._id || el.id}
                                    index={index}
                                />
                            ))}
                    </div>

                    {!noExperience && certificateFields.map((field, index) => (
                        <CertificateInput
                            stored={storedExperience.length}
                            key={field.id}
                            control={control}
                            register={register}
                            getValues={getValues}
                            watch={watch}
                            id={field.id}
                            remove={certificateRemove}
                            name="experience"
                            index={index}
                            errors={errors}
                            hideFile={true}
                            locationLabel="Company"
                        />
                    ))}

                    {!noExperience && (
                        <button
                            type="button"
                            className='mb-4 flex justify-center m-auto w-full text-center'
                            onClick={() => { addEmptyCertificate(); }}
                        >
                            <FiPlusCircle size={30} color='#191d51' fontWeight={700} />
                        </button>
                    )}

                    <button disabled={isLoading} className="block rounded-xl mx-auto px-5 py-2 bg-nt-blue text-white self-center mt-5">
                        Save
                    </button>
                </form>
            </div>
        </div>
    );
};

export { TutorExperience };
